import { ParticipationRateSearchCriteria } from "domain/entity/ParticipationRate/ParticipationRateSearchCriteria";
import { ActiveIndDroOpts } from "presentation/constant/DropDownOptions/Common/ActiveIndDroOpts";
import { ParticipationRateMaintenanceConstant } from "presentation/constant/ParticipationRate/ParticipationRateMaintenanceConstant";
import { PortConstant } from "presentation/constant/Port/PortConstant";
import { useParticipationRateMaintenanceVM } from "presentation/hook/ParticipationRate/useParticipationRateMaintenanceVM";
import { useParticipationRateMaintenanceTracked } from "presentation/store/ParticipationRate/ParticipationRateMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, HPHInputMask, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const ParticipationRateSearchPanel = () => {
    const SCREEN_CONSTANT = ParticipationRateMaintenanceConstant;
    const [participationRateState] = useParticipationRateMaintenanceTracked();
    const participationRateVM = useParticipationRateMaintenanceVM();
    const { searchCriteria } = participationRateState;
    const [isLoading, setIsLoading] = useState(false);

    const resetButtonClicked = useCallback(() => {
        participationRateVM.onSearchCriteriaResetClick();
    }, [participationRateVM]);

    const searchButtonClicked = useCallback((searchCriteria: ParticipationRateSearchCriteria) => {
        setIsLoading(true);
        participationRateVM.onSearch(true, searchCriteria).then((data) => {
            setIsLoading(false);
        }).catch(error => {
            setIsLoading(false);
        })

    }, [participationRateVM])

    const memoEtdFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.ETD_DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "etdFrom", endField: "etdTo" }}
                dateRange={{ startDate: searchCriteria.etdFrom, endDate: searchCriteria.etdTo }}
                onDatesChange={participationRateVM.onSearchDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.ETD_DATE_RANGE, searchCriteria.etdFrom, searchCriteria.etdTo, participationRateVM.onSearchDateRangeChange])

    const memoSoa = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.soa ?? ''}
                fieldValue={searchCriteria?.soa}
                fieldLabel={SCREEN_CONSTANT.SOA}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'soa'}
                maxLength={4}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    participationRateVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.soa, SCREEN_CONSTANT.SOA, participationRateVM])

    const memoCoVslVoy = useMemo(() =>
        <div className='flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <HPHInputMask
                width='470px'
                label={SCREEN_CONSTANT.CO_VSL_VOY}
                autoFocus={false}
                value={{
                    co: searchCriteria.co ?? '',
                    vsl: searchCriteria.vsl ?? '',
                    voy: searchCriteria.voy ?? ''
                }}
                fieldConfiguration={{
                    co: { maxLength: 4, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    vsl: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    voy: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                }}
                onDataChange={(e: any) => participationRateVM.onSearchCoVslVoyChange(e, {
                    co: 'co',
                    vsl: 'vsl',
                    voy: 'voy'
                })} />
        </div>
        , [SCREEN_CONSTANT.CO_VSL_VOY, searchCriteria.co, searchCriteria.vsl, searchCriteria.voy, participationRateVM])

    const memoActiveInd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.activeInd || ''}
                fieldValue={searchCriteria?.activeInd}
                fieldLabel={SCREEN_CONSTANT.ACTIVE_IND}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'activeInd'}
                maxLength={60}
                options={ActiveIndDroOpts().getDroOptsModel()}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    participationRateVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.ACTIVE_IND, participationRateVM, searchCriteria?.activeInd])


    return (
        <div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px" }}>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{PortConstant.SEARCH_CRITERIA}</SidebarTitle>
                        <SidebarActionCross>
                            <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={participationRateVM.onSearchClick} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>

            {isLoading && <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ maxHeight: '75vh', height: '75vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>

                        <CriteriaItemContainer>
                            {memoCoVslVoy}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoEtdFromTo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoSoa}
                            {memoActiveInd}
                        </CriteriaItemContainer>

                    </div>
                </div>
            </div>
            <SidebarActionBar>
                <HPHButton label={'Reset'} size={'Small'} theme={'Secondary'} onClick={resetButtonClicked} />
                <HPHButton label={'Search'} size={'Small'} theme={'Primary'} onClick={() => searchButtonClicked(searchCriteria)} />
            </SidebarActionBar>
        </div>
    );
}

export default memo(ParticipationRateSearchPanel);
